// Pure helpers shared by PrescriptionImportPage. Translation maps and label
// adapters live here so the page index stays under the 100-line component
// budget. No React, no hooks — just plain functions.
import type { ApiPrescriptionZone } from 'types/prescription';

import type { UploadState } from './hooks/useData/types';

import {
    EARTH_RADIUS_METERS,
    MIN_RING_POINTS,
    SQ_METERS_PER_HECTARE
} from './components/ShapefilePreview/constants';
import type { BoundaryStat } from './components/SideRail/types';
import type { UploadCardLabels } from './components/UploadCard/types';

const HECTARES_DECIMAL_PLACES = 2;

const METERS_PER_DEGREE = 111320;

const COORDINATE_DECIMAL_PLACES = 2;

type Translations = {
    APPROX_AREA_RAW?: string;
    CONTINUE_CTA: string;
    DROP_ZONE_CTA: string;
    DROP_ZONE_CTA_PROCESSING: string;
    DROP_ZONE_FORMATS: string;
    GAP_POLICY_BASE_RATE: string;
    GAP_POLICY_MASK: string;
    PAGE_CANCEL: string;
    PREVIEW_APPROX_AREA: string;
    PREVIEW_AREA_VALUE: (hectares: string) => string;
    PREVIEW_CRS: string;
    PREVIEW_CRS_VALUE: (check: string) => string;
    PREVIEW_FILE: string;
    PREVIEW_RATE_COLUMN: string;
    PREVIEW_RATE_RANGE: string;
    PREVIEW_RATE_SINGLE: (rate: string, unit: string) => string;
    PREVIEW_RATE_SPAN: (min: string, max: string, unit: string) => string;
    PREVIEW_ZONES: string;
    RAIL_BOUNDARY_EMPTY_BODY: string;
    RAIL_BOUNDARY_READY_BOUNDING_BOX_LABEL: string;
    RAIL_BOUNDARY_READY_BOUNDING_BOX_VALUE: (width: string, height: string) => string;
    RAIL_BOUNDARY_READY_CENTROID_LABEL: string;
    RAIL_BOUNDARY_READY_CENTROID_VALUE: (lat: string, lng: string) => string;
    RAIL_BOUNDARY_READY_ZONES_LABEL: string;
    RAIL_BOUNDARY_READY_ZONES_VALUE: (count: number) => string;
    RAIL_BOUNDARY_TITLE: string;
    RAIL_NEXT_BODY: string;
    RAIL_NEXT_READY_BODY: string;
    RAIL_NEXT_READY_TITLE: string;
    RAIL_NEXT_TITLE: string;
    REPLACE_FILE_CTA: string;
    SECTION_1_EMPTY_HINT: string;
    SECTION_1_EMPTY_TITLE: string;
    SECTION_1_FILLED_TITLE: string;
    SECTION_2_EYEBROW: string;
    SECTION_2_HINT: string;
    SECTION_2_TITLE: string;
    SECTION_EYEBROW_STEP_1: string;
    SUBMITTING_CTA: string;
};

export type FilledData = {
    approxAreaHectares: string;
    boundingBoxHeightMeters: string;
    boundingBoxWidthMeters: string;
    centroidLat: string;
    centroidLng: string;
    fileName: string;
    rateColumn: string;
    zoneCount: number;
    zones: ApiPrescriptionZone[];
};

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

const ringAreaSqMeters = (ring: number[][]): number => {
    if (ring.length < MIN_RING_POINTS) {
        return 0;
    }
    let total = 0;

    for (let i = 0; i < ring.length; i += 1) {
        const [lng1, lat1] = ring[i];
        const [lng2, lat2] = ring[(i + 1) % ring.length];

        total +=
            toRadians(lng2 - lng1) * (2 + Math.sin(toRadians(lat1)) + Math.sin(toRadians(lat2)));
    }

    return Math.abs((total * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS) / 2);
};

const computeZonesAreaHectares = (zones: ApiPrescriptionZone[]): number => {
    let total = 0;

    for (const zone of zones) {
        for (const ring of zone.coordinates) {
            total += ringAreaSqMeters(ring);
        }
    }

    return total / SQ_METERS_PER_HECTARE;
};

type ZoneBounds = {
    centroidLat: number;
    centroidLng: number;
    heightMeters: number;
    widthMeters: number;
};

const computeZoneBounds = (zones: ApiPrescriptionZone[]): ZoneBounds => {
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    for (const zone of zones) {
        for (const ring of zone.coordinates) {
            for (const [lng, lat] of ring) {
                if (lat < minLat) {
                    minLat = lat;
                }

                if (lat > maxLat) {
                    maxLat = lat;
                }

                if (lng < minLng) {
                    minLng = lng;
                }

                if (lng > maxLng) {
                    maxLng = lng;
                }
            }
        }
    }

    if (!Number.isFinite(minLat)) {
        return { centroidLat: 0, centroidLng: 0, heightMeters: 0, widthMeters: 0 };
    }
    const centroidLat = (minLat + maxLat) / 2;
    const centroidLng = (minLng + maxLng) / 2;
    const heightMeters = (maxLat - minLat) * METERS_PER_DEGREE;
    const widthMeters = (maxLng - minLng) * METERS_PER_DEGREE * Math.cos(toRadians(centroidLat));

    return { centroidLat, centroidLng, heightMeters, widthMeters };
};

const EMPTY_FILLED_DATA: FilledData = {
    approxAreaHectares: '0',
    boundingBoxHeightMeters: '0',
    boundingBoxWidthMeters: '0',
    centroidLat: '0',
    centroidLng: '0',
    fileName: '',
    rateColumn: '',
    zoneCount: 0,
    zones: []
};

export const computeFilledData = ({ uploadState }: { uploadState: UploadState }): FilledData => {
    if (uploadState.kind !== 'readyToSubmit') {
        return EMPTY_FILLED_DATA;
    }
    const { fileName, rateColumn, zones } = uploadState;
    const hectares = computeZonesAreaHectares(zones);
    const bounds = computeZoneBounds(zones);

    return {
        approxAreaHectares: hectares.toFixed(HECTARES_DECIMAL_PLACES),
        boundingBoxHeightMeters: Math.round(bounds.heightMeters).toString(),
        boundingBoxWidthMeters: Math.round(bounds.widthMeters).toString(),
        centroidLat: bounds.centroidLat.toFixed(COORDINATE_DECIMAL_PLACES),
        centroidLng: bounds.centroidLng.toFixed(COORDINATE_DECIMAL_PLACES),
        fileName,
        rateColumn,
        zoneCount: zones.length,
        zones
    };
};

export const buildUploadCardLabels = (t: Translations): UploadCardLabels => ({
    approxArea: t.PREVIEW_APPROX_AREA,
    cancel: t.PAGE_CANCEL,
    continueCta: t.CONTINUE_CTA,
    crs: t.PREVIEW_CRS,
    crsValue: t.PREVIEW_CRS_VALUE,
    dropZoneCta: t.DROP_ZONE_CTA,
    dropZoneCtaProcessing: t.DROP_ZONE_CTA_PROCESSING,
    dropZoneFormats: t.DROP_ZONE_FORMATS,
    file: t.PREVIEW_FILE,
    gapPolicyBaseRate: t.GAP_POLICY_BASE_RATE,
    gapPolicyMask: t.GAP_POLICY_MASK,
    previewAreaValue: t.PREVIEW_AREA_VALUE,
    rateColumn: t.PREVIEW_RATE_COLUMN,
    rateRange: t.PREVIEW_RATE_RANGE,
    rateSingle: t.PREVIEW_RATE_SINGLE,
    rateSpan: t.PREVIEW_RATE_SPAN,
    replaceFile: t.REPLACE_FILE_CTA,
    section1EmptyHint: t.SECTION_1_EMPTY_HINT,
    section1EmptyTitle: t.SECTION_1_EMPTY_TITLE,
    section1FilledTitle: t.SECTION_1_FILLED_TITLE,
    section2Eyebrow: t.SECTION_2_EYEBROW,
    section2Hint: t.SECTION_2_HINT,
    section2Title: t.SECTION_2_TITLE,
    sectionEyebrowStep1: t.SECTION_EYEBROW_STEP_1,
    submittingCta: t.SUBMITTING_CTA,
    zones: t.PREVIEW_ZONES
});

export type SideRailViewProps = {
    boundaryEmptyBody?: string;
    boundaryStats?: BoundaryStat[];
    boundaryTitle: string;
    nextStepsBody: string;
    nextStepsTitle: string;
};

export const buildSideRailProps = ({
    filledData,
    isFilled,
    t
}: {
    filledData: FilledData;
    isFilled: boolean;
    t: Translations;
}): SideRailViewProps => {
    if (!isFilled) {
        return {
            boundaryEmptyBody: t.RAIL_BOUNDARY_EMPTY_BODY,
            boundaryTitle: t.RAIL_BOUNDARY_TITLE,
            nextStepsBody: t.RAIL_NEXT_BODY,
            nextStepsTitle: t.RAIL_NEXT_TITLE
        };
    }

    const stats: BoundaryStat[] = [
        {
            label: t.RAIL_BOUNDARY_READY_ZONES_LABEL,
            value: t.RAIL_BOUNDARY_READY_ZONES_VALUE(filledData.zoneCount)
        },
        {
            label: t.RAIL_BOUNDARY_READY_BOUNDING_BOX_LABEL,
            value: t.RAIL_BOUNDARY_READY_BOUNDING_BOX_VALUE(
                filledData.boundingBoxWidthMeters,
                filledData.boundingBoxHeightMeters
            )
        },
        {
            label: t.RAIL_BOUNDARY_READY_CENTROID_LABEL,
            value: t.RAIL_BOUNDARY_READY_CENTROID_VALUE(
                filledData.centroidLat,
                filledData.centroidLng
            )
        }
    ];

    return {
        boundaryStats: stats,
        boundaryTitle: t.RAIL_BOUNDARY_TITLE,
        nextStepsBody: t.RAIL_NEXT_READY_BODY,
        nextStepsTitle: t.RAIL_NEXT_READY_TITLE
    };
};
